// istanbul ignore file
import _ from 'lodash';
import { createSelector } from 'reselect';

// Stable empty fallbacks — reused so selectors always get the same reference
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

/** * BASE SELECTORS
 * We update getModules and getLessons to check the modulesList and progressList paths
 * where redux-api actually stores the data.
 */
const getAccounts = state => state.accounts?.all || EMPTY_ARRAY;

const getModules = state => {
  return state.modulesList?.data || state.content?.modules || state.modules || EMPTY_ARRAY;
};

const getModuleListLoading = state => state.modulesList?.loading;
const getModuleListError = state => state.modulesList?.error;

const getLessons = state => state.content?.lessons || state.lessons || EMPTY_ARRAY;
const getProgressObjects = state => state.progressList?.data || state.progress?.all || EMPTY_ARRAY;
const getDischargeProgressObjects = state => state.dischargeProgress?.all || EMPTY_ARRAY;

/** * NAVIGATION & PARAM SELECTORS
 */
const getCurrentModuleSlug = (state, props) => 
  props?.match?.params?.moduleSlug || state.currentModuleSlug;
const getCurrentModulePageSlug = (state, props) => props?.match?.params?.pageSlug || state.currentModulePageSlug;
const getCurrentLessonSlug = (state, props) => props?.match?.params?.lessonSlug || state.currentLessonSlug;
const getCurrentTopicSlug = (state, props) =>
  props?.match?.params?.partSlug || props?.match?.params?.pageNumber || state.currentTopicSlug;
const getCurrentPageNumber = (state, props) =>
  Number(props?.match?.params?.pageNumber);

const getModuleIntroPages = state => state.moduleIntroPages?.data || state.moduleIntroPages || EMPTY_ARRAY;
const getModulePages = state => state.content?.introPages;
const getLessonPages = state => state.content?.lessonPages || state.lessonPages || EMPTY_OBJECT;
const getQuestions = state => state.questions;
const getDiaryEntries = state => state.entries?.all || EMPTY_ARRAY;
const getDischargeSteps = state => state.dischargeSteps?.data || state.dischargeSteps || EMPTY_ARRAY;

/** * RECORD BASE SELECTORS
 */
const getBasicRecords = state => state.basicRecords || EMPTY_OBJECT;
const getGrowthRecords = state => state.growthRecords || EMPTY_OBJECT;
const getGrowthRecordsLoading = state => state.growthRecordsLoading;
const getDietDiaperRecords = state => state.dietDiaperRecords || EMPTY_OBJECT;
const getDietDiaperRecordsLatest = state => state.dietDiaperRecordsLatest?.data;
const getMilestoneRecords = state => state.milestoneRecords || EMPTY_OBJECT;
const getSleepRecords = state => state.sleepRecords || EMPTY_OBJECT;

/**
 * PROFILE & BABY SELECTORS
 */
const getProfilesById = state => state.profilesById || state.profile?.all || EMPTY_OBJECT;
const getSelectedBabyId = state => state.selectedBaby?.id || state.ui?.selectedBabyId || state.selectedBabyId;

/**
 * ENHANCEMENT HELPER
 */
const enhanceRecords = (records, babyId, profiles) => {
  return _.chain(records)
    .filter(record => String(record.babyId) === String(babyId))
    .map(record => {
      const profile = profiles[record.providerId];
      const providerName = profile 
        ? `${profile.firstName} ${profile.lastName}` 
        : (record.otherProvider || '');

      return {
        ...record,
        provider: providerName,
        providerId: record.otherProvider ? 'other' : record.providerId
      };
    })
    .keyBy('id')
    .value();
};

/**
 * ENHANCED EXPORTS
 */
const getEnhancedBasicRecords = createSelector(
  getBasicRecords,
  getSelectedBabyId,
  getProfilesById,
  enhanceRecords
);

const getEnhancedGrowthRecords = createSelector(
  getGrowthRecords,
  getSelectedBabyId,
  getProfilesById,
  enhanceRecords
);

const getEnhancedDietDiaperRecords = createSelector(
  getDietDiaperRecords,
  getSelectedBabyId,
  getProfilesById,
  enhanceRecords
);

const getEnhancedMilestoneRecords = createSelector(
  getMilestoneRecords,
  getSelectedBabyId,
  getProfilesById,
  enhanceRecords
);

const getEnhancedSleepRecords = createSelector(
  getSleepRecords,
  getSelectedBabyId,
  getProfilesById,
  enhanceRecords
);

/**
 * UTILITY SELECTORS
 */
const getModuleIdToLessonPageIdsMap = createSelector(
  getModules,
  getLessons,
  (modules, lessons) =>
    _.reduce(
      modules,
      (resultObject, module) => {
        const moduleLessonPages = _.reduce(
          lessons,
          (resultArray, lesson) => {
            if (lesson.moduleId === module.id) {
              return [...resultArray, ...lesson.pages];
            }
            return resultArray;
          },
          []
        );
        return { ...resultObject, [module.id]: moduleLessonPages };
      },
      {}
    )
);

export {
  getModuleListError,
  getModuleListLoading,
  getAccounts,
  getModules,
  getLessons,
  getProgressObjects,
  getDischargeProgressObjects,
  getCurrentModuleSlug,
  getCurrentModulePageSlug,
  getCurrentLessonSlug,
  getCurrentTopicSlug,
  getCurrentPageNumber,
  getModulePages,
  getModuleIntroPages,
  getLessonPages,
  getModuleIdToLessonPageIdsMap,
  getQuestions,
  getBasicRecords,
  getGrowthRecords,
  getDietDiaperRecords,
  getMilestoneRecords,
  getSleepRecords,
  getDiaryEntries,
  getDischargeSteps,
  getGrowthRecordsLoading,
  getDietDiaperRecordsLatest,
  getEnhancedBasicRecords,
  getEnhancedGrowthRecords,
  getEnhancedDietDiaperRecords,
  getEnhancedMilestoneRecords,
  getEnhancedSleepRecords,
  getProfilesById,
  getSelectedBabyId
};
